/**
 * Terminal Component
 * Lazy-loaded Xterm.js integration with WebSocket PTY connection
 */

import { getToken } from '../utils/storage.js';
import { getActiveMachine, getMachineBaseURL } from '../utils/config.js';
import { debug } from '../utils/debug.js';

// Active terminal instances
const terminals = new Map();
let xtermLoaded = false;
let Terminal = null;
let FitAddon = null;

/**
 * Load Xterm.js (one-time initialization)
 */
async function loadXterm() {
    if (xtermLoaded) return;

    try {
        // Dynamically import Xterm.js modules (resolved via import map in index.html)
        const [xtermModule, fitAddonModule] = await Promise.all([
            import('xterm'),
            import('@xterm/addon-fit')
        ]);

        Terminal = xtermModule.Terminal;
        FitAddon = fitAddonModule.FitAddon;
        xtermLoaded = true;
        debug.log('✅ Xterm.js loaded');
    } catch (err) {
        console.error('❌ Failed to load Xterm:', err);
        throw err;
    }
}

/**
 * Create and mount a terminal instance
 * @param {string} containerId - DOM element ID to mount terminal
 * @returns {string} Terminal session ID
 */
export async function createTerminal(containerId) {
    const container = document.getElementById(containerId);
    if (!container) {
        console.error('Terminal container not found:', containerId);
        return null;
    }

    // Load Xterm.js if not loaded
    try {
        await loadXterm();
    } catch (error) {
        console.error('Failed to load Xterm.js:', error);
        return null;
    }

    // Create Xterm instance
    const term = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: '"Cascadia Code", Consolas, monospace',
        theme: {
            background: '#1e1e1e',
            foreground: '#d4d4d4',
            cursor: '#ffffff',
            selection: '#264f78',
        },
        rows: 24,
        cols: 80,
    });

    // Add fit addon
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);

    // Mount to DOM
    term.open(container);
    fitAddon.fit();

    // Connect WebSocket
    const sessionId = connectWebSocket(term);
    
    // Store instance
    const ws = term._ws || null;
    delete term._ws; // Clean up temp property

    // --- Clipboard hotkeys (Ctrl+V / Ctrl+Shift+V paste, Ctrl+Shift+C copy) ---
    term.attachCustomKeyEventHandler((e) => {
        if (e.type !== 'keydown') return true;

        // Ctrl+V or Ctrl+Shift+V → paste from clipboard
        if (e.ctrlKey && (e.key === 'v' || e.key === 'V')) {
            if (navigator.clipboard) {
                navigator.clipboard.readText().then(text => {
                    if (text && ws && ws.readyState === WebSocket.OPEN) {
                        ws.send(text);
                    }
                }).catch(err => {
                    console.warn('Clipboard read denied:', err);
                });
            } else {
                console.warn('Clipboard API not available (non-secure context?)');
            }
            return false;
        }

        // Ctrl+Shift+C → copy selection (Ctrl+C without Shift falls through to SIGINT)
        if (e.ctrlKey && e.shiftKey && e.key === 'C') {
            const selection = term.getSelection();
            if (!selection) return true; // No selection — pass through
            if (navigator.clipboard) {
                navigator.clipboard.writeText(selection).catch(err => {
                    console.warn('Clipboard write denied:', err);
                });
            } else {
                console.warn('Clipboard API not available (non-secure context?)');
            }
            return false;
        }

        return true; // All other keys pass through
    });

    terminals.set(sessionId, {
        term,
        fitAddon,
        container,
        sessionId,
        ws: ws,
    });

    // Handle window resize
    const resizeObserver = new ResizeObserver(() => {
        if (container.offsetParent !== null) { // Only if visible
            fitAddon.fit();
        }
    });
    resizeObserver.observe(container);
    
    term.focus(); // Ensure focus

    debug.log('✅ Terminal created:', sessionId);
    return sessionId;
}

/**
 * Connect WebSocket to backend PTY
 * @param {Terminal} term - Xterm instance
 * @returns {string} Session ID
 */
function connectWebSocket(term) {
    const machine = getActiveMachine();
    const token = getToken(machine?.name || 'default');
    
    if (!machine || !token) {
        term.writeln('\x1b[1;31mError: Not authenticated\x1b[0m');
        return null;
    }

    // Build WebSocket URL from machine base URL
    const baseURL = getMachineBaseURL(machine);
    const machineName = machine?.name || 'Unknown';
    const machineUrl = machine?.url || `${window.location.origin}${baseURL || ''}`;
    let wsUrl;
    if (baseURL && baseURL.startsWith('http')) {
        // Remote machine or proxied machine with full URL (e.g. "https://railway.app/gcp")
        const url = new URL(baseURL);
        const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        const pathPrefix = url.pathname.replace(/\/+$/, ''); // e.g. "/gcp" or ""
        wsUrl = `${wsProtocol}//${url.host}${pathPrefix}/api/ws/terminal?cols=${term.cols}&rows=${term.rows}`;
    } else {
        // Self machine — baseURL is a path prefix (e.g. "/car" or "")
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProtocol}//${window.location.host}${baseURL}/api/ws/terminal?cols=${term.cols}&rows=${term.rows}`;
    }

    debug.log('Connecting to:', wsUrl);
    
    // Pass JWT via Sec-WebSocket-Protocol header for reliable auth on all browsers
    // (iOS Safari ITP can suppress cookies on WebSocket upgrade requests).
    // The server echoes back the subprotocol in the 101 response per RFC 6455.
    const ws = new WebSocket(wsUrl, [`auth-token.${token}`]);
    ws.binaryType = 'arraybuffer'; // Ensure we get ArrayBuffers, not Blobs

    const sessionId = `terminal-${Date.now()}`;

    ws.onopen = () => {
        debug.log('✅ WebSocket connected');
        term.writeln(`\x1b[1;32mConnected to ${machineName} (${machineUrl})\x1b[0m`);
        
        // Send input to backend
        term.onData(data => {
            debug.log('Term input:', JSON.stringify(data)); // Debug input
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(data);
            } else {
                console.warn('WS not open, cannot send');
            }
        });

        // Send resize to backend
        term.onResize(({ cols, rows }) => {
            debug.log('PTY resize request:', cols, rows);
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'resize', cols, rows }));
            }
        });
    };

    ws.onmessage = (event) => {
        // Write output from backend
        const data = event.data;
        if (typeof data === 'string') {
            term.write(data);
        } else if (data instanceof ArrayBuffer) {
            term.write(new Uint8Array(data));
        } else {
            console.warn('Unknown terminal data type:', typeof data);
        }
    };

    ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        term.writeln('\x1b[1;31mConnection error\x1b[0m');
    };

    ws.onclose = () => {
        debug.log('WebSocket closed');
        term.writeln('\x1b[1;33m\nConnection closed\x1b[0m');
    };

    // Store WebSocket - will be retrieved by createTerminal
    // Save it temporarily for createTerminal to pick up
    term._ws = ws;

    return sessionId;
}

/**
 * Destroy a terminal instance
 * @param {string} sessionId - Terminal session ID
 */
export function destroyTerminal(sessionId) {
    const instance = terminals.get(sessionId);
    if (!instance) return;

    // Close WebSocket
    if (instance.ws) {
        instance.ws.close();
    }

    // Dispose Xterm
    instance.term.dispose();

    // Remove from map
    terminals.delete(sessionId);
    
    debug.log('🗑️ Terminal destroyed:', sessionId);
}

/**
 * Resize terminal to fit container
 * @param {string} sessionId - Terminal session ID
 */
export function resizeTerminal(sessionId) {
    const instance = terminals.get(sessionId);
    if (!instance) return;

    instance.fitAddon.fit();
}

/**
 * Get active terminal instance
 * @param {string} sessionId - Terminal session ID
 * @returns {object|null} Terminal instance
 */
export function getTerminal(sessionId) {
    return terminals.get(sessionId) || null;
}
